import { SquarePen, Loader2, X, Pencil, Plus, ExternalLink } from "lucide-react";
import {
  Box,
  Text,
  TextField,
  IconButton,
  Button,
  Flex,
} from "@radix-ui/themes";
import { useField } from "formik";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, useEffect, useRef } from "react";
import { IEntity } from "@/types/breeze/Entity";
import { formatColumnName, singularize } from "@/utils/text";
import EntityDetailView from "@/components/entity/EntityDetailView";
import { anyService } from "@/services/breeze/AnyService";
import SideDrawer from "@/components/ui/SideDrawer";
import ManagedEntityGrid from "@/components/entity/ManagedEntityGrid";
import EntityFormV2, {
  EntityFormHandle,
} from "@/components/entity/EntityFormV2";
import { cn } from "@/utils/cn";
import { AnyValue } from "@/types/breeze/Any";

type SidebarMode = "none" | "select-existing" | "create-new";

const getEntityId = (value: unknown): number | undefined => {
  if (!value) return undefined;
  if (typeof value === "object" && value !== null && value && "id" in value) {
    return (value as { id: number }).id;
  }
  return Number(value);
};

interface EntityRecordSelectProps {
  entity: IEntity;
  name: string;
  description?: string;
  label: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  isEditing?: boolean; // Parent component edit mode
  individualEditAllowed?: boolean; // Whether individual field editing is allowed
  // Parent record context for relationship management
  parentRecordId?: number;
  parentEntityName?: string;
  onChange?: (value: AnyValue) => void;
  onEditSaved?: (name: string, value: AnyValue) => void;
  onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

export default function EntityRecordSelect({
  entity,
  name,
  description,
  label,
  required = false,
  className,
  disabled,
  isEditing = true,
  individualEditAllowed = false,
  parentRecordId: _parentRecordId,
  parentEntityName: _parentEntityName,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: EntityRecordSelectProps) {
  const [field, { error, touched }, helpers] = useField(name);

  const [isIndividualEditing, setIsIndividualEditing] = useState(false);
  const [tempValue, setTempValue] = useState<AnyValue | null>(null);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>("none");
  const [nestedEntity, setNestedEntity] = useState<{
    entityName: string;
    entityId: number;
  } | null>(null);
  const formRef = useRef<EntityFormHandle>(null);

  const entityId = useMemo(() => getEntityId(field.value), [field.value]);

  const nameField = entity.fields.find((f) => f.is_name_field)?.name;

  useEffect(() => {
    // Initialize temp value when sidebar opens
    if (sidebarMode !== "none" && tempValue === null) {
      setTempValue(field.value);
    }
  }, [sidebarMode, field.value, tempValue]);

  // Reset individual editing state when parent edit mode changes
  useEffect(() => {
    if (isEditing) {
      setIsIndividualEditing(false);
    }
  }, [isEditing]);

  const { data: record, isLoading } = useQuery({
    queryKey: ["entity-data", entity.name, entityId],
    queryFn: () => anyService.getById(entity.name, entityId!),
    enabled: !!entity.name && !!entityId,
  });

  const handleEdit = () => {
    setIsIndividualEditing(true);
    setTempValue(field.value);
    setSidebarMode("select-existing");
    onIndividualEditModeChanged?.(true);
  };

  const handleFieldClick = () => {
    // Always open the sidebar when the field is clicked (regardless of edit mode)
    setTempValue(field.value);
    setSidebarMode("select-existing");
  };

  const handleSave = async () => {
    // Update the form field value
    helpers.setValue(tempValue);

    // Call onChange only when saving, not during selection
    onChange?.(tempValue);

    // Only call onEditSaved if we're in individual editing mode
    if (isIndividualEditing) {
      onEditSaved?.(name, tempValue);
      setIsIndividualEditing(false);
      onIndividualEditModeChanged?.(false);
    }

    // Reset temp value and close sidebar
    setTempValue(null);
    setSidebarMode("none");
  };

  const handleCancel = () => {
    // Reset temp value and close sidebar
    setTempValue(null);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
    setSidebarMode("none");
  };

  const handleClearValue = () => {
    // Always just update temp value when clearing during selection
    if (sidebarMode !== "none") {
      setTempValue(null);
    } else if (isIndividualEditing) {
      setTempValue(null);
    } else {
      // Only directly update form field if we're not in selection mode
      field.onChange({ target: { name: field.name, value: null } });
      onChange?.(null);
    }
  };

  const handleRecordSelected = (recordId: AnyValue) => {
    if (tempValue === recordId) {
      setTempValue(null);
    } else {
      setTempValue(recordId);
    }
  };

  const handleCreateNew = () => {
    setSidebarMode("create-new");
  };

  // Determine current field state
  const shouldShowEditIcon =
    !isEditing && individualEditAllowed && !isIndividualEditing;
  const isReadonlyMode = !isEditing && !isIndividualEditing;

  // Get display value
  const displayValue = nameField
    ? String(record?.[nameField] || "")
    : record?.id;

  return (
    <Box mb="4" width="100%" className={className}>
      <Box mb="2">
        {label && (
          <div className="flex items-center justify-between">
            <Text as="label" htmlFor={name} size="2" weight="medium" mb="1">
              {label} {required && <Text color="red">{" *"}</Text>}
            </Text>
          </div>
        )}

        {description && (
          <Text size="1" color="gray" mb="1">
            {description}
          </Text>
        )}
      </Box>

      <TextField.Root
        className={cn(
          isReadonlyMode && "readonly-input",
          ((isReadonlyMode && individualEditAllowed) ||
            (isEditing && !isIndividualEditing)) &&
            "cursor-pointer",
        )}
        style={{
          flexGrow: 1,
          cursor:
            (isReadonlyMode && individualEditAllowed) ||
            (isEditing && !isIndividualEditing)
              ? "pointer"
              : undefined,
        }}
        id={name}
        type="text"
        placeholder="Select a value"
        readOnly={isReadonlyMode}
        disabled={disabled || isLoading}
        value={displayValue || ""}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled && !isLoading) {
            if (isReadonlyMode && individualEditAllowed) {
              handleEdit();
            } else if (isEditing && !isIndividualEditing) {
              handleFieldClick();
            }
          }
        }}
      >
        {isReadonlyMode && record && !isLoading && entityId ? (
          <TextField.Slot side="right">
            <Flex gap="2" align="center">
              <IconButton
                size="1"
                type="button"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  if (entityId) {
                    setNestedEntity({
                      entityName: entity.name,
                      entityId,
                    });
                  }
                }}
                aria-label="View record details"
              >
                <ExternalLink className="w-4 h-4" />
              </IconButton>
              {shouldShowEditIcon && (
                <IconButton
                  size="1"
                  type="button"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  aria-label="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </IconButton>
              )}
            </Flex>
          </TextField.Slot>
        ) : isReadonlyMode && shouldShowEditIcon ? (
          <TextField.Slot side="right">
            <IconButton
              size="1"
              type="button"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
              aria-label="Edit"
            >
              <Pencil className="w-4 h-4" />
            </IconButton>
          </TextField.Slot>
        ) : isEditing || isIndividualEditing ? (
          <>
            <TextField.Slot
              side="right"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled && !isLoading) {
                  handleFieldClick();
                }
              }}
              style={{ paddingRight: 0 }}
            >
              {isLoading ? (
                <Loader2
                  width="16"
                  height="16"
                  style={{
                    animation: "spin 1s linear infinite",
                    cursor: "pointer",
                  }}
                />
              ) : (
                <SquarePen
                  data-testid="select-record-button"
                  width="16"
                  height="16"
                  style={{ cursor: "pointer" }}
                />
              )}
            </TextField.Slot>
            {record && !isLoading && (
              <TextField.Slot
                side="right"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearValue();
                }}
                style={{ cursor: "pointer", paddingLeft: "8px" }}
              >
                <X width="16" height="16" />
              </TextField.Slot>
            )}
          </>
        ) : null}
      </TextField.Root>

      {error && touched && (
        <Text color="red" size="1" mt="1">
          {error}
        </Text>
      )}

      <div style={{ maxWidth: "50vw", width: "100%" }}>
        <SideDrawer
          isOpen={sidebarMode !== "none"}
          onClose={handleCancel}
          title={
            sidebarMode === "create-new"
              ? `Create New ${singularize(entity.name)}`
              : `Select ${singularize(entity.name)}`
          }
          width="w-full"
          saveButton={
            sidebarMode === "create-new"
              ? {
                  label: "Save",
                  onClick: () => {
                    formRef.current?.saveChanges();
                  },
                }
              : sidebarMode === "select-existing"
                ? {
                    label: "Save Selection",
                    onClick: handleSave,
                  }
                : undefined
          }
        >
          {sidebarMode === "select-existing" && (
            <Box>
              <Flex gap="2" mb="4" mt="2" justify="start">
                <Button
                  type="button"
                  size="2"
                  variant="soft"
                  onClick={handleCreateNew}
                  style={{
                    backgroundColor: "var(--gray-3)",
                    color: "var(--gray-11)",
                  }}
                >
                  <Plus size={16} style={{ marginRight: "6px" }} />
                  Create New
                </Button>
              </Flex>

              <Text
                size="2"
                color="gray"
                mb="4"
                style={{ display: "block", lineHeight: "1.5" }}
              >
                Select an existing {singularize(entity.name).toLowerCase()} from
                the list below, or create a new one.
              </Text>

              <ManagedEntityGrid
                entityName={entity.name}
                disableFieldSelector
                rowSelection="single"
                selectedRecordIds={
                  getEntityId(tempValue) ? [getEntityId(tempValue)!] : []
                }
                onCellClicked={(data) => {
                  handleRecordSelected(data.id);
                }}
                onSelectionChanged={(selectedRows) => {
                  // Handle checkbox selection changes
                  if (selectedRows.length === 0) {
                    setTempValue(null);
                  } else {
                    setTempValue(selectedRows[0].id);
                  }
                }}
              />
            </Box>
          )}

          {sidebarMode === "create-new" && (
            <Box>
              <Text
                size="2"
                color="gray"
                mb="4"
                style={{ display: "block", lineHeight: "1.5" }}
              >
                Create a new {singularize(entity.name).toLowerCase()} and add it
                to this relationship.
              </Text>

              <EntityFormV2
                ref={formRef}
                entity={entity}
                mode="create"
                userHasUpdatePermission={true}
                splitFieldsIntoGrid={false}
                onCreateSuccess={(createdRecord) => {
                  // Stage the newly created item
                  setTempValue(createdRecord.id);
                  // Return to selection view so user can confirm or choose differently
                  setSidebarMode("select-existing");
                }}
              />
            </Box>
          )}
        </SideDrawer>
      </div>

      {nestedEntity && (
        <SideDrawer
          isOpen={true}
          onClose={() => setNestedEntity(null)}
          title={`${singularize(formatColumnName(nestedEntity.entityName))} Details`}
          width="max-w-2xl"
        >
          <EntityDetailView
            entityName={nestedEntity.entityName}
            entityId={nestedEntity.entityId}
          />
        </SideDrawer>
      )}
    </Box>
  );
}
